import { clearSession, readSession, updateSession } from '@/lib/auth-storage';
import { currentCompanyScope } from '@/stores/company-scope-store';
import type { AuthTokens } from '@/types/api';

export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly payload?: unknown;
  readonly correlationId?: string;

  constructor(status: number, message: string, payload?: unknown, correlationId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    this.correlationId = correlationId;
  }

  /** True for the failures a form should render inline rather than as a toast. */
  get isValidation() {
    return this.status === 400 || this.status === 409 || this.status === 422;
  }
}

type Listener = () => void;
const sessionExpiryListeners = new Set<Listener>();

/** Fires once the refresh token is spent — the app tears the session down. */
export function onSessionExpired(fn: Listener): () => void {
  sessionExpiryListeners.add(fn);
  return () => sessionExpiryListeners.delete(fn);
}

function expireSession() {
  clearSession();
  sessionExpiryListeners.forEach((fn) => fn());
}

/**
 * A single in-flight refresh shared by every caller. The backend rotates refresh
 * tokens and revokes all sessions on replay, so two parallel refreshes would log
 * the user out — this collapses them into one.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    const session = readSession();
    if (!session) return null;
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      if (!res.ok) {
        expireSession();
        return null;
      }
      const tokens = (await res.json()) as AuthTokens;
      updateSession(tokens);
      return tokens.accessToken;
    } catch {
      return null;
    } finally {
      // Cleared on the next tick so callers awaiting this promise still see it.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

/**
 * Only `Vehicle` and `User` carry `toJSON: { virtuals: true }` on the backend, so
 * every other collection arrives as `_id`. Normalising here means components can
 * assume `id` everywhere instead of branching per resource.
 */
export function normalizeIds<T>(input: T): T {
  if (Array.isArray(input)) return input.map(normalizeIds) as unknown as T;
  if (input === null || typeof input !== 'object') return input;
  if (input instanceof Blob || input instanceof Date) return input;

  const source = input as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    if (key === '__v') continue;
    if (key === '_id') {
      // A populated ref arrives as an object; keep it, but do not let it
      // overwrite an `id` the virtual already produced.
      if (typeof value === 'string' && out.id === undefined && source.id === undefined) {
        out.id = value;
      }
      continue;
    }
    out[key] = normalizeIds(value);
  }
  return out as T;
}

/** Any plain object; resource query interfaces have no index signature. */
export type QueryParams = object;

/**
 * A token the caller can hand straight to a long-lived consumer such as the
 * WebSocket handshake. Refreshes first when the stored one is at or near
 * expiry: the socket's auth callback runs outside the fetch path, so it would
 * otherwise present a stale token, get rejected by the gateway and retry
 * forever with the same dead credential.
 */
export async function ensureFreshToken(): Promise<string | null> {
  const session = readSession();
  if (!session) return null;
  // 30s of slack so a token cannot expire in flight.
  if (session.expiresAt - Date.now() > 30_000) return session.accessToken;
  return refreshAccessToken();
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: QueryParams | undefined;
  /** Skip the Authorization header — login, refresh, password reset. */
  anonymous?: boolean;
}

export function buildQuery(query?: QueryParams): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((v) => v !== undefined && v !== null && params.append(key, String(v)));
    } else if (value instanceof Date) {
      params.append(key, value.toISOString());
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function toApiError(res: Response): Promise<ApiError> {
  let payload: unknown;
  let message = res.statusText || `Request failed with ${res.status}`;
  try {
    payload = await res.json();
    const body = payload as { message?: string | string[]; correlationId?: string };
    if (Array.isArray(body?.message)) message = body.message.join(', ');
    else if (typeof body?.message === 'string') message = body.message;
    return new ApiError(res.status, message, payload, body?.correlationId);
  } catch {
    return new ApiError(res.status, message, payload);
  }
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, anonymous, headers, ...rest } = options;

  const send = async (token: string | null): Promise<Response> => {
    const finalHeaders = new Headers(headers);
    if (body !== undefined && !(body instanceof FormData)) {
      finalHeaders.set('Content-Type', 'application/json');
    }
    if (token) finalHeaders.set('Authorization', `Bearer ${token}`);

    // Only a platform admin may name another company; the backend rejects the
    // header from anyone else, so it is set unconditionally when a scope is
    // chosen rather than being gated on the role here.
    const scope = anonymous ? null : currentCompanyScope();
    if (scope) finalHeaders.set('X-Company-Id', scope.id);

    return fetch(`${API_BASE}${path}${buildQuery(query)}`, {
      ...rest,
      headers: finalHeaders,
      body:
        body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    });
  };

  const token = anonymous ? null : (readSession()?.accessToken ?? null);
  let res = await send(token);

  // One retry behind a refresh. `anonymous` calls never retry: a 401 there is
  // a genuine credential failure, not an expired access token.
  if (res.status === 401 && !anonymous) {
    const fresh = await refreshAccessToken();
    if (!fresh) {
      expireSession();
      throw await toApiError(res);
    }
    res = await send(fresh);
  }

  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await res.blob()) as T;
  return normalizeIds((await res.json()) as T);
}

export const api = {
  get: <T>(path: string, query?: QueryParams, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
